import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api, ApiError } from '@/lib/api'
import type { AdminPermissionKey } from '@/constants/permissions'

interface User {
  _id: string
  fullName: string
  email: string
  role: string
  isSuperAdmin: boolean
  permissions: string[]
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hasPermission: (key: AdminPermissionKey) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem('admin_user')
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, _setUser] = useState<User | null>(readCachedUser)
  const [loading, setLoading] = useState(true)

  const setUser = useCallback((newUser: User | null) => {
    if (newUser) {
      localStorage.setItem('admin_user', JSON.stringify(newUser))
    } else {
      localStorage.removeItem('admin_user')
    }
    _setUser(newUser)
  }, [])

  const loadPermissions = async (): Promise<{ isSuperAdmin: boolean; permissions: string[] }> => {
    try {
      const perms = await api.admin.getMyPermissions()
      return perms
    } catch {
      return { isSuperAdmin: false, permissions: [] }
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      api.users
        .getMe()
        .then(async (u) => {
          const userData = u as unknown as User
          if (userData.role !== 'admin') {
            localStorage.removeItem('admin_token')
            setUser(null)
          } else {
            const perms = await loadPermissions()
            setUser({
              ...userData,
              isSuperAdmin: perms.isSuperAdmin,
              permissions: perms.permissions,
            })
          }
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 401) {
            localStorage.removeItem('admin_token')
            setUser(null)
          }
          // Network error → token хэвэл үлдэнэ
        })
        .finally(() => setLoading(false))
    } else {
      setUser(null)
      setLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const res = await api.auth.login({ email, password })
    const userData = res.user as unknown as User
    if (userData.role !== 'admin') {
      throw new Error('Админ эрхгүй байна')
    }
    localStorage.setItem('admin_token', res.token)
    const perms = await loadPermissions()
    setUser({
      ...userData,
      isSuperAdmin: perms.isSuperAdmin,
      permissions: perms.permissions,
    })
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    setUser(null)
  }

  const hasPermission = useCallback(
    (key: AdminPermissionKey): boolean => {
      if (!user) return false
      if (user.isSuperAdmin) return true
      return user.permissions.includes(key)
    },
    [user]
  )

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
